import React from 'react';
import { useQueryState } from 'react-router-use-location-state';
import { useDispatch } from 'react-redux';
import { alpha, styled } from '@mui/material';
import { type GridEventListener } from '@mui/x-data-grid-premium';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { showConfirmCancelDialog, Utils } from 'app/main/components';
import { useMountEffect, usePagination, useSelection, useSettings, useUserData } from 'app/main/components/hooks';
import { DetailsAdditionalPropsContext, ViewTable } from 'app/main/components/page-layouts';
import type { ViewFooterCustomInfoProps } from 'app/main/components/page-layouts/view/components/ViewFooter';
import type { MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';

import { getRowClassName } from '../../components/OrdersLinesTableUtils';
import OrdersLinesTableFooterInfo from '../../components/OrdersLinesTableFooterInfo';
import useOrdersLinesTableColumns from '../../lines/OrdersLinesTableColumns';
import OrdersLinesAddItemDialog from '../../lines/OrdersLinesAddItemDialog';
import OrdersLinesAddNoteDialog from '../../lines/OrdersLinesAddNoteDialog';
import OrdersLinesAddStandardNotesDialog from '../../lines/OrdersLinesAddStandardNotesDialog';

interface OrdersLinesTabProps {
	order: NonNullable<any>;
	updateRefresh: VoidFunction;
}

const StyledViewTable = styled(ViewTable)(({ theme }) => ({
	'.OrderLinesTab-UpdatedPriceRow, .OrderLinesTab-UpdatedPriceRow.Mui-selected': {
		'&.MuiDataGrid-row': {
			backgroundColor: alpha(theme.palette.warning.light, 0.2),
			'&:hover': {
				backgroundColor: alpha(theme.palette.warning.light, 0.3)
			}
		}
	},
	'.OrderLinesTab-UnavailableRow, .OrderLinesTab-UnavailableRow.Mui-selected': {
		'&.MuiDataGrid-row': {
			backgroundColor: alpha(theme.palette.error.main, 0.2),
			'&:hover': {
				backgroundColor: alpha(theme.palette.error.main, 0.3)
			}
		}
	}
}));

export default function OrdersLinesTab({ order, updateRefresh }: OrdersLinesTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);

	const defaultFilters: MiddlewhereFilter[] = [
		{ filt_field: 'orli_ords_uid', filt_clause: 'equals', filt_value: order.ords_uid }
	];
	const paginationProps = usePagination({ module: 'orders_lines', filter: { defaultFilters } });
	const { paginatorProps } = paginationProps;

	const isEditable = order.ords_orst_id === undefined || [1, 4, 5].includes(order.ords_orst_id);
	const canUpdate = authorizer.can('update', 'orders') && isEditable;

	const [openItemsAddDialog, setOpenItemsAddDialog] = useQueryState('openItemsAddDialog', false);
	const [openNoteAddDialog, setOpenNoteAddDialog] = React.useState(false);
	const [openStandardNotesAddDialog, setOpenStandardNotesAddDialog] = React.useState(false);

	const { changeAdditionalProp } = React.useContext<any>(DetailsAdditionalPropsContext);

	const { selectedItems: selectedLines, setSelectedItems: setSelectedLines } = useSelection({
		module: 'orders_lines',
		isSelecting: canUpdate,
		editCallback: newSelectedLines => changeAdditionalProp?.('selectedOrderLines', newSelectedLines || [])
	});

	const columns = useOrdersLinesTableColumns({ order, isEditable, selectedLines, paginatorProps, updateRefresh });

	const showDeleteLinesDialog = (lines: any[]) => {
		dispatch(
			showConfirmCancelDialog({
				title: lines?.length > 1 ? t('DIALOG_TITLE_DELETE_MULTIPLE') : t('DIALOG_TITLE_DELETE_GENERIC'),
				onConfirm: () => {
					dispatch(Utils.alertProgressMsg(t));
					Utils.executeStored({
						action: 'st_orders_lines_delete',
						dispatch,
						data: {
							ords_uid: order.ords_uid,
							ids: lines.map(orli => orli.orli_uid)
						},
						successHandler: () => {
							dispatch(Utils.alertSuccessMsg(t));
							setSelectedLines([]);
							updateRefresh();
						}
					});
				}
			})
		);
	};

	useMountEffect(() => {
		changeAdditionalProp('setSelectedLines', setSelectedLines);
		changeAdditionalProp('openLinesAddDialog', () => setOpenItemsAddDialog(true));
		changeAdditionalProp('openNoteAddDialog', () => setOpenNoteAddDialog(true));
		changeAdditionalProp('openStandardNotesAddDialog', () => setOpenStandardNotesAddDialog(true));
		changeAdditionalProp('showDeleteLinesDialog', showDeleteLinesDialog);
	});

	const FooterCustomInfo = React.useCallback(
		({ additionalInfo }: ViewFooterCustomInfoProps) => {
			const isMerchActive = order.ords_merchandise_active === 'true';
			const totTaxExclPromosDiscount = additionalInfo.tot_value_tax_excl_promos_discounts || 0;
			const merchPointRatio = order.ords_merchpoints_ratio || 0;
			const totMerchPoints = isMerchActive ? Math.ceil(totTaxExclPromosDiscount * merchPointRatio) : undefined;

			return (
				<OrdersLinesTableFooterInfo
					totTaxExcl={additionalInfo.tot_value_tax_excl}
					totTaxExclPromosDiscount={totTaxExclPromosDiscount}
					totMerchPoints={totMerchPoints}
				/>
			);
		},
		[order.ords_merchandise_active, order.ords_merchpoints_ratio]
	);

	return (
		<>
			<StyledViewTable
				id="ordersLinesView"
				module="orders_lines"
				columns={columns}
				paginationProps={paginationProps}
				slots={{ footerCustomInfo: FooterCustomInfo }}
				getRowClassName={({ row }) => getRowClassName(row, isEditable)}
				selectionProps={canUpdate ? { mode: 'multiple', selectedItems: selectedLines } : undefined}
			/>
			<OrdersLinesAddItemDialog
				openDialog={openItemsAddDialog}
				closeDialog={() => setOpenItemsAddDialog(false)}
				orderUid={order.ords_uid}
				compUid={order.ords_comp_uid}
				pricingGroupId={order.ords_pric_id}
				discountPercentage={order.disc_percentage}
				existingRows={order.ords_lines}
				updateRefresh={updateRefresh}
			/>
			<OrdersLinesAddNoteDialog
				openDialog={openNoteAddDialog}
				closeDialog={() => setOpenNoteAddDialog(false)}
				orderUid={order.ords_uid}
				updateRefresh={updateRefresh}
			/>
			<OrdersLinesAddStandardNotesDialog
				openDialog={openStandardNotesAddDialog}
				closeDialog={() => setOpenStandardNotesAddDialog(false)}
				orderUid={order.ords_uid}
				updateRefresh={updateRefresh}
			/>
		</>
	);
}
